import React from 'react';

import { beginImport, endImport, uploadAndParse } from '../api/ecgViewerApi.js';
import { compactRecording, MAX_SESSION_BYTES } from '../utils/compactRecording.js';
export default function useEcgImports() {
  const [imports, setImports] = React.useState([]);
  const [recordings, setRecordings] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const active = React.useRef(null);
  const retained = React.useRef(0);
  const nextImportNumber = React.useRef(1);
  React.useEffect(() => () => active.current?.abort(), []);
  const start = React.useCallback(async (selection, selectionInfo = {}) => {
    if (active.current) return;
    const all = Array.from(selection || []).map((entry) =>
      entry.file
        ? entry
        : {
            file: entry,
            relativePath: entry.webkitRelativePath || entry.name,
          }
    );
    const files = all
      .filter((entry) => /\.scp$/i.test(entry.file.name))
      .sort((a, b) => a.relativePath.localeCompare(b.relativePath, undefined, { numeric: true }));
    const number = nextImportNumber.current;
    nextImportNumber.current += 1;
    const id = `import-${number}`;
    if (!files.length) {
      setImports((previous) => [
        {
          id,
          number,
          date: new Date().toLocaleString(),
          total: 0,
          uploaded: 0,
          success: 0,
          failed: 0,
          skipped: all.length,
          status: 'No SCP files',
          errors: [],
        },
        ...previous,
      ]);
      return;
    }
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    const summary = {
      id,
      number,
      date: new Date().toLocaleString(),
      total: files.length,
      uploaded: 0,
      success: 0,
      failed: 0,
      skipped: all.length - files.length + (selectionInfo.skipped || 0),
      available: selectionInfo.available || files.length,
      rangeStart: selectionInfo.rangeStart,
      rangeEnd: selectionInfo.rangeEnd,
      status: 'Importing',
      errors: [],
    };
    const publish = () =>
      setImports((previous) => [
        { ...summary, errors: [...summary.errors] },
        ...previous.filter((entry) => entry.id !== summary.id),
      ]);
    publish();
    let serverImportId;
    try {
      serverImportId = await beginImport(files.length, controller.signal);
      for (const entry of files) {
        const { file, relativePath: path } = entry;
        if (controller.signal.aborted) break;
        try {
          if (retained.current >= MAX_SESSION_BYTES) {
            summary.status = 'Stopped: viewer memory limit';
            break;
          }
          const uploadFile = entry.loadFile ? await entry.loadFile(controller.signal) : file;
          if (controller.signal.aborted) break;
          const result = await uploadAndParse(uploadFile, controller.signal, serverImportId);
          if (controller.signal.aborted) break;
          summary.uploaded += 1;
          const { recording: compact, byteLength: size } = compactRecording(result);
          if (retained.current + size > MAX_SESSION_BYTES)
            throw new Error('Session memory limit reached. Clear session before importing more.');
          retained.current += size;
          const recording = {
            ...compact,
            path,
            id: `${summary.id}:${summary.success}`,
            importId: summary.id,
          };
          setRecordings((previous) =>
            [...previous, recording].sort(
              (a, b) =>
                (a.recordedAt || '').localeCompare(b.recordedAt || '') ||
                a.path.localeCompare(b.path, undefined, { numeric: true })
            )
          );
          summary.success += 1;
        } catch (error) {
          if (controller.signal.aborted) break;
          // A 422 response means the upload arrived but parsing was rejected.
          if (error.response?.status === 422) summary.uploaded += 1;
          summary.failed += 1;
          summary.errors.push({
            path,
            message: error.response?.data?.error || error.message || 'Import failed',
          });
          if (
            [401, 403, 409, 429].includes(error.response?.status) ||
            error.message?.includes('Session memory limit')
          ) {
            summary.status = 'Stopped';
            break;
          }
        }
        publish();
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        summary.status = 'Could not start import';
        summary.errors.push({
          path: 'Import',
          message: error.response?.data?.error || error.message,
        });
      }
    } finally {
      if (serverImportId) await endImport(serverImportId).catch(() => undefined);
    }
    summary.status = controller.signal.aborted
      ? 'Cancelled'
      : summary.status === 'Importing'
        ? 'Complete'
        : summary.status;
    publish();
    active.current = null;
    setBusy(false);
  }, []);
  const cancel = React.useCallback(() => active.current?.abort(), []);
  const clear = React.useCallback(() => {
    if (active.current) return;
    retained.current = 0;
    setImports([]);
    setRecordings([]);
  }, []);
  return { imports, recordings, busy, start, cancel, clear };
}
